import React, { useState, useEffect, useCallback, useMemo } from "react";
import { withTranslation, Trans } from "react-i18next";
import { bindActionCreators } from "redux";
import { useSelector, useDispatch } from "react-redux";
import moment from "moment";
import WithSelect from "../../../components/select";
import StatusBadge from "../../../components/statusbadge/StatusBadge";
import * as downloadJobsActions from "../../../modules/downloadJobs";
import * as notificationActions from "../../../modules/notification";
import * as globalActions from "../../../modules/global";
import { CONSTANTS } from "../../../constants";
import DownloadInfoMessage from "@routes/sources/components/DownloadInfoMessage";
import ObjectIcon from "@routes/sources/components/ObjectIcon";
import { getDownloadJobTask, getDownloadJobTaskAPI } from "../../../modules/downloadJobs";
import StorageDownloadCdnTooltip from "../../../components/tooltip/StorageDownloadCdnTooltip";
import { alpha, Box, Tooltip } from "@mui/material";
import {
    IconZip,
    IconChevronDown,
    IconChevronUp,
    IconErrorCircle,
    IconDownload,
    IconEmptyData,
    IconPath,
    IconPlay,
    IconRetry,
    IconSlow,
    IconAssetsAdd,
    IconCircle,
    IconOpenInNew,
    IconArrowDown,
    IconArrowUp,
    IconProjects,
    IconCategory,
    ImageFolderSmallOn,
    IconHierarchyFolded,
} from "@mzc-cloudplex/icons";
import theme from "@styles/theme";
import {
    Button,
    Checkbox,
    Chip,
    Collapse,
    Divider,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Stack,
} from "@mzc-pdc/ui";
import { SelectLimitTags } from "@components_v2/select";
import JobEmptyList from "@routes/rightsidebar/ui/job-empty-list";
import State from "@components/state";
import { Link } from "react-router-dom";
import menuService from "@services/menuService";
import Typography from "@components_v2/typography/typography";
import AssetTypeFlag from "@components_v2/icon-type/asset/asset-type-flag";
import { GridNameEllipsis } from "@components_v2/grid/grid.styled";

let throttleHandler = false;

const DownloadJobPanel = (props) => {
    const { t } = props;
    const dispatch = useDispatch();
    const [limit, setLimit] = useState(20);
    const [viewmoreEnable, setViewmoreEnable] = useState(false);
    const [filterType, setFilterType] = useState([]);
    const [filterStatus, setFilterStatus] = useState([]);

    const { downloadJobs } = useSelector((state) => state);
    const stage = useSelector(({ stage }) => stage);
    const project = useSelector(({ project }) => project);

    const DownloadJobsActions = bindActionCreators(downloadJobsActions, dispatch);
    const NotificationActions = bindActionCreators(notificationActions, dispatch);
    const GlobalActions = bindActionCreators(globalActions, dispatch);

    useEffect(() => {
        if (downloadJobs.totalCounts > downloadJobs.data.length) {
            setViewmoreEnable(true);
        } else {
            setViewmoreEnable(false);
        }
    }, [downloadJobs.data]);

    // useEffect(() => {
    //     DownloadJobsActions.updateSearch({ limit });
    // }, [limit]);

    const onChangeLimit = useCallback(
        async (limit) => {
            await DownloadJobsActions.updateSearch({ limit });
            await DownloadJobsActions.getDownloadJobs();
            setLimit(limit);
        },
        [limit],
    );

    const onSubmit = useCallback(async () => {
        await DownloadJobsActions.updateSearch({ limit: downloadJobs.search.limit + limit });
        await DownloadJobsActions.getDownloadJobs();
    }, []);

    const onRetry = useCallback(
        async (jobId) => {
            await DownloadJobsActions.getDownloadJobTask({ jobId });
            const downloadJob = downloadJobs.data.find((v) => v.jobId === jobId);
            const domain = downloadJob?.metadata?.domain;
            const targets = downloadJob?.metadata?.targets;

            if (!downloadJob || !targets || targets.length <= 0) return;

            try {
                if (domain === "STORAGE") {
                    //TODO: 폴더 재시도의 경우 NotFound 파일을 찾아서 제외시켜야 함
                    const options = {
                        shouldZip: downloadJob?.metadata?.shouldZip,
                    };
                    const errorTargets = downloadJob?.metadata?.errorTargets;
                    const newTargets = targets
                        .map((item) => {
                            const bucket = item.bucket;
                            const errorKeys = errorTargets.filter((et) => et.bucket === bucket).map((et) => et.key);
                            const newKeys = item.keys.filter((k) => !errorKeys.includes(k));
                            return { ...item, keys: newKeys };
                        })
                        .filter((t) => t.keys.length > 0);

                    if (newTargets.length == 0) return;

                    const response = await DownloadJobsActions.createDownloadJob(domain, newTargets, options);
                    const newJobId = response?.data?.id;
                    const newJobStatus = response?.data?.status;
                    await DownloadJobsActions.getDownloadJobs();

                    let newFolders = [];
                    targets.forEach((target) => {
                        target.keys.forEach((key) => {
                            const splitPath = key.slice(0, key.lastIndexOf("/") + 1);
                            newFolders.push(target.bucket + splitPath);
                        });
                    });

                    const fileName = targets[0].keys[0].slice(targets[0].keys[0].lastIndexOf("/") + 1);

                    if (newJobId && newJobStatus) {
                        const jobTasksResponse = await DownloadJobsActions.getDownloadJobTask({ jobId: newJobId });
                        const tasks = jobTasksResponse?.data?.results || [];
                        for (let i = 0; i < tasks.length; i++) {
                            const task = tasks[i];
                            if (task.status === downloadJobsActions.JMS_STATUS.SUCCEED)
                                await DownloadJobsActions.downloadCompressedFile(newJobId, task.name);
                        }
                    }

                    NotificationActions.setNotification({
                        type: "info",
                        autoClose: false,
                        contents: (
                            <DownloadInfoMessage
                                folders={newFolders}
                                fileName={fileName}
                                totalCount={newFolders.length}
                                isDirectDownloadRequest={downloadJob?.metadata?.isDirectDownloadRequest}
                            />
                        ),
                    });
                } else {
                    const downloadRequestTargets = {
                        resourceId: downloadJob.metadata?.resourceId,
                        projectId: downloadJob.metadata?.projectId,
                        resourceName: targets[0]?.assets[0]?.name,
                    };

                    await DownloadJobsActions.createDownloadJob(domain, downloadRequestTargets, 1);

                    await DownloadJobsActions.getDownloadJobs();
                    NotificationActions.setNotification({
                        type: "info",
                        autoClose: false,
                        contents: (
                            <DownloadInfoMessage
                                folders={""}
                                fileName={targets[0]?.assets[0]?.name}
                                totalCount={1}
                                false
                            />
                        ),
                    });
                }
            } catch (error) {
                console.log(error);
                // toastErrorMessage(error);
                // await MyJobsActions.getMyJobs();
            }
        },
        [downloadJobs.data],
    );

    const toggleJobIsExpanded = useCallback(async (item) => {
        if (throttleHandler) {
            return;
        }
        if (item.isFolded) {
            const job = {
                ...item,
                isFolded: !item.isFolded,
            };
            await DownloadJobsActions.updateJob({ job });
        } else {
            throttleHandler = true;
            try {
                const { data } = await DownloadJobsActions.getDownloadJobTask({ jobId: item.jobId });
                // console.log('get my tasks', data);
                const newTasks = data.results || [];
                const job = {
                    ...item,
                    isFolded: !item.isFolded,
                    tasks: newTasks,
                };
                await DownloadJobsActions.updateJob({ job });
                throttleHandler = false;
            } catch (error) {
                throttleHandler = false;
            }
        }
    }, []);

    const renderJobStatus = useCallback(
        (item) => {
            // console.log('check items:', item)
            let className, icon, text;
            switch (item.status) {
                case "SUBMITTED":
                    className = "text-primary";
                    icon = "progress";
                    text = t(`common::label::${"Preparing {{type}}..."}`, { type: "download" });
                    break;
                case "PROGRESSING":
                    className = "text-primary";
                    icon = "progress";
                    text = t(`common::label::${"Preparing {{type}}..."}`, { type: "download" });
                    break;
                case "SUCCEED":
                    className = item.partialFailure ? "text-danger" : "text-success";
                    text = item.partialFailure
                        ? item.summary?.detail?.failCount === item.taskCount
                            ? t(`common::label::${"{{type}} error."}`, {
                                  type: t(`common::label::download`, `download`),
                              })
                            : t(`common::label::${"Partial {{type}} to completed."}`, {
                                  type: t(`common::label::download`, `download`),
                              })
                        : t(`common::label::${"Ready to {{type}}"}`, {
                              type: t(`common::label::download`, `download`),
                          });
                    break;
                case "FAILED":
                    className = "text-danger";
                    text = t(`common::label::${"{{type}} error"}`, { type: t(`common::label::download`, `download`) });
                    break;
                default:
                    className = "text-primary";
                    text = item.action === "MOVE" ? `${t(`common::label::${"Moving"}`)}...` : "";
                    break;
            }
            return (
                <React.Fragment>
                    <small className={`${className}`}>
                        {icon && <i className="sprite sprite-progressing" />}
                        <span>{`${text}`}</span>
                    </small>
                </React.Fragment>
            );
        },
        [downloadJobs.data],
    );

    const getFileCount = useCallback(
        (item) => {
            if (!downloadJobs.data) return "";

            if (item.metadata.domain === "STORAGE") {
                const targets = item?.metadata?.targets;
                const target = targets[0];
                let fileCount = 0;

                targets.forEach((t) => (fileCount = fileCount + t.keys.length));

                if (item.metadata?.isDirectDownloadRequest === "ZIPPING") {
                    return fileCount;
                } else {
                    return item.taskCount;
                }
            } else {
                return 1;
            }
        },
        [downloadJobs.data],
    );

    const onClickDownload = async (jobId, taskName) => {
        await DownloadJobsActions.downloadCompressedFile(jobId, taskName);
    };

    const onClickJobDownload = useCallback(async (job) => {
        const jobId = job.jobId;
        const response = await DownloadJobsActions.getDownloadJobTask({ jobId });
        const tasks = response?.data?.results;

        for (let i = 0; i < tasks.length; i++) {
            const task = tasks[i];
            if (task.status === downloadJobsActions.JMS_STATUS.SUCCEED) await onClickDownload(jobId, task.name);
        }
    });

    const getJobStatus = useCallback(
        (item) => {
            if (!item || !downloadJobs.data) return "";
            const status = item.status;
            const partialFailure = item.partialFailure;

            if (status === "FAILED" || item.summary?.detail?.failCount === item.taskCount) return "ERROR";
            else return status;
        },
        [downloadJobs.data],
    );

    const getJobFileIcon = useCallback((item, task) => {
        if (item?.metadata?.domain === "STORAGE") {
            if (item?.metadata?.shouldZip) {
                return <IconZip size={16} htmlColor={`#9fa3b2`} />;
            }
            return (
                <ObjectIcon
                    path={task?.metadata?.archiveFileName}
                    isFolder={task?.metadata && task?.metadata?.isFolder}
                    isFolderDisabled={task?.metadata && task?.metadata?.isFolder}
                />
            );
        } else {
            return <AssetTypeFlag width={20} height={16} type={item?.metadata?.targets[0]?.assets[0]?.mediaType} />;
        }
    }, []);

    const getJobRepresentiveName = useCallback((item) => {
        if (item.metadata.domain === "STORAGE") {
            return item.metadata.targets[0].keys[0];
        } else {
            return item.metadata.targets[0].assets[0].name;
        }
    }, []);

    const getJobFileName = useCallback((item, task) => {
        if (item.status === "FAILED") {
            return `${t(`common::label::Download`, `Download`)} ${t(`common::label::error`, `error`)}`;
        } else {
            return task?.metadata?.archiveFileName
                ? task?.metadata?.archiveFileName.substring(
                      task?.metadata?.archiveFileName.lastIndexOf("/") + 1,
                      task?.metadata?.archiveFileName.length,
                  )
                : item?.metadata?.shouldZip
                ? "Compressing"
                : "Preparing";
        }
    }, []);

    const downloadJobType = useMemo(() => [{ label: "Download", value: "STORAGE" }], []);

    const filteredJobsData = useMemo(() => {
        const parsedFilterType = filterType.map(
            (typeLabel) => downloadJobType.find((type) => type.label === typeLabel)?.value ?? typeLabel,
        );

        const parsedFilterStatus = filterStatus.map(
            (typeLabel) => CONSTANTS("ASSET_JOB_STATUS").find((type) => type.label === typeLabel)?.value ?? typeLabel,
        );

        return downloadJobs.data.filter((job) => {
            if (parsedFilterType.length > 0 && !parsedFilterType.includes(job.action)) {
                return false;
            }
            if (parsedFilterStatus.length > 0 && !parsedFilterStatus.includes(getJobStatus(job))) {
                return false;
            }
            return true;
        });
    }, [downloadJobs.data, filterType, filterStatus, downloadJobType, getJobStatus]);

    return (
        <React.Fragment>
            <Grid container pt={3} px={3} pb={2} gap={1} justifyContent={"center"} alignItems={"center"}>
                <Grid item xs>
                    <FormControl fullWidth variant="outlined">
                        <InputLabel id="select-label-asset-jobs-type" shrink={true}>
                            {t(`common::label::Type`, `Type`)}
                        </InputLabel>
                        <SelectLimitTags
                            displayEmpty
                            notched
                            label={"Type"}
                            labelId={"select-label-asset-jobs-type"}
                            multiple={true}
                            value={filterType}
                            MenuProps={{
                                disablePortal: true,
                            }}
                            onChange={(e) => {
                                setFilterType(e.target.value);
                            }}
                            limitTags={1}
                        >
                            {downloadJobType.map((status) => (
                                <MenuItem
                                    key={`asset-job-type-${status.value}`}
                                    value={status.label}
                                    sx={{ borderRadius: `0 !important`, borderBottom: "none !important" }}
                                >
                                    <Checkbox color={"greySecondary"} checked={filterType.includes(status.label)} />
                                    {t(`common::label::${status.label}`, status.label)}
                                </MenuItem>
                            ))}
                        </SelectLimitTags>
                    </FormControl>
                </Grid>
                <Grid item xs>
                    <FormControl fullWidth variant="outlined">
                        <InputLabel id="select-label-asset-jobs-status" shrink={true}>
                            {t(`common::label::Status`, `Status`)}
                        </InputLabel>
                        <SelectLimitTags
                            displayEmpty
                            notched
                            label={"Status"}
                            labelId={"select-label-asset-jobs-status"}
                            multiple={true}
                            value={filterStatus}
                            MenuProps={{
                                disablePortal: true,
                            }}
                            onChange={(e) => {
                                setFilterStatus(e.target.value);
                            }}
                            limitTags={1}
                        >
                            {CONSTANTS("ASSET_JOB_STATUS").map((status) => (
                                <MenuItem
                                    key={`asset-job-status-${status.value}`}
                                    value={status.label}
                                    sx={{
                                        borderRadius: `0 !important`,
                                        borderBottom: "none !important ",
                                    }}
                                >
                                    <Checkbox color={"greySecondary"} checked={filterStatus.includes(status.label)} />
                                    {t(`common::label::${status.label}`, status.label)}
                                </MenuItem>
                            ))}
                        </SelectLimitTags>
                    </FormControl>
                </Grid>
            </Grid>
            <Divider sx={{ mx: 3 }} />
            <Stack flex={1} px={3} py={2.5} gap={4}>
                {filteredJobsData.length > 0 ? (
                    <>
                        {filteredJobsData.map((item) => {
                            return (
                                <Box>
                                    <Grid container pb={1} gap={1} alignItems={"center"}>
                                        <Grid item>
                                            <Grid
                                                container
                                                alignItems={"center"}
                                                justifyContent={"center"}
                                                width={24}
                                                height={20}
                                                bgcolor={"greySecondary.main"}
                                                color={"greySecondary.contrastText"}
                                                borderRadius={0.7}
                                            >
                                                <IconDownload size={16} />
                                            </Grid>
                                        </Grid>
                                        <Divider flexItem orientation={"vertical"} sx={{ my: 1 }} />
                                        <Grid item xs>
                                            <Grid container gap={1} alignItems={"center"}>
                                                <State variant={"contained"} status={getJobStatus(item)} />
                                                {(item.status === "FAILED" || item.partialFailure) && (
                                                    <Tooltip
                                                        title={
                                                            item.summary?.exitCode !== "SUCCESS"
                                                                ? t(
                                                                      `common::code::${item.summary.exitCode}`,
                                                                      item.summary.exitCode,
                                                                  )
                                                                : t(
                                                                      `common::label::This operation was not entirely successful.`,
                                                                      "This operation was not entirely successful.",
                                                                  )
                                                        }
                                                    >
                                                        <IconButton circled size="small">
                                                            <IconErrorCircle size={16} color="error" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Grid>
                                        </Grid>
                                        <Grid item>
                                            {(getJobStatus(item) === "FAILED" || item.partialFailure) && (
                                                <Button
                                                    variant={"text"}
                                                    size={"small"}
                                                    color={"greySecondary"}
                                                    startIcon={<IconRetry size={16} />}
                                                    onClick={() => onRetry(item.jobId)}
                                                >
                                                    {t(`common::label::Retry`, `Retry`)}
                                                </Button>
                                            )}
                                        </Grid>
                                    </Grid>
                                    <Divider />
                                    <Stack py={2.5} px={1.5} gap={1.5}>
                                        <Grid container gap={1}>
                                            <Grid item xs={3}>
                                                <Grid container gap={1} alignItems={"center"}>
                                                    <IconCircle size={6} htmlColor={theme.palette.greySecondary[500]} />
                                                    {t(`common::label::Job`, `Job`)}
                                                </Grid>
                                            </Grid>
                                            <Grid item>{t(`common::label::Download`, `Download`)}</Grid>
                                        </Grid>
                                        <Grid container gap={1}>
                                            <Grid item xs={3}>
                                                <Grid container gap={1} alignItems={"center"}>
                                                    <IconCircle size={6} htmlColor={theme.palette.greySecondary[500]} />
                                                    {t(`common::label::Job ID`, `Job ID`)}
                                                </Grid>
                                            </Grid>
                                            <Grid item>{item.jobId ?? "-"}</Grid>
                                        </Grid>
                                        {item.startAt && (
                                            <Grid container gap={1}>
                                                <Grid item xs={3}>
                                                    <Grid container gap={1} alignItems={"center"}>
                                                        <IconCircle
                                                            size={6}
                                                            htmlColor={theme.palette.greySecondary[500]}
                                                        />
                                                        {t(`common::label::Start date`, `Start date`)}
                                                    </Grid>
                                                </Grid>
                                                <Grid item>{`${moment(item.startAt).format(
                                                    "YYYY-MM-DD HH:mm:ss",
                                                )}`}</Grid>
                                            </Grid>
                                        )}
                                        {item.endAt && (
                                            <Grid container gap={1}>
                                                <Grid item xs={3}>
                                                    <Grid container gap={1} alignItems={"center"}>
                                                        <IconCircle
                                                            size={6}
                                                            htmlColor={theme.palette.greySecondary[500]}
                                                        />
                                                        {t(`common::label::End date`, `End date`)}
                                                    </Grid>
                                                </Grid>
                                                <Grid item>{`${moment(item.endAt).format(
                                                    "YYYY-MM-DD HH:mm:ss",
                                                )}`}</Grid>
                                            </Grid>
                                        )}
                                    </Stack>
                                    <Collapse in={!!item.isFolded} collapsedSize={75}>
                                        <Box
                                            px={1}
                                            py={1.5}
                                            borderRadius={1}
                                            bgcolor={alpha(theme.palette.greySecondary[50], 0.5)}
                                            maxHeight={500}
                                            overflow={"auto"}
                                        >
                                            <Grid container gap={1.5}>
                                                <Grid item>
                                                    <Tooltip
                                                        title={t(
                                                            `common::label::${!item.isFolded ? "Fold" : "Expand"}`,
                                                            !item.isFolded ? "Fold" : "Expand",
                                                        )}
                                                    >
                                                        <IconButton
                                                            circled
                                                            size="xxsmall"
                                                            onClick={() => toggleJobIsExpanded(item)}
                                                            sx={{ width: 20, height: 20 }}
                                                        >
                                                            {!item.isFolded ? (
                                                                <IconArrowDown size={12} />
                                                            ) : (
                                                                <IconArrowUp size={12} />
                                                            )}
                                                        </IconButton>
                                                    </Tooltip>
                                                </Grid>
                                                <Grid item xs>
                                                    {item.taskCount > 0 && (
                                                        <Stack gap={1.5}>
                                                            <Grid container>
                                                                <Grid item xs>
                                                                    <Grid
                                                                        container
                                                                        mt={0.25}
                                                                        mb={1.5}
                                                                        alignItems={"center"}
                                                                    >
                                                                        {!(
                                                                            item.status === "SUCCEED" ||
                                                                            item.status === "FAILED"
                                                                        ) &&
                                                                            item.metadata?.isDirectDownloadRequest ===
                                                                                "MISS_MATCH_REGION" && (
                                                                                <StorageDownloadCdnTooltip />
                                                                            )}
                                                                        <Trans
                                                                            i18nKey="common::msg::Detailed view of <0> {{number}} </0> tasks"
                                                                            values={{ number: item.taskCount || 0 }}
                                                                            components={[
                                                                                <Typography
                                                                                    variant={"body3_700"}
                                                                                    color="primary.main"
                                                                                ></Typography>,
                                                                            ]}
                                                                        />
                                                                    </Grid>
                                                                    <Grid container gap={0.75} alignItems={"center"}>
                                                                        {!(
                                                                            item.status === "SUCCEED" ||
                                                                            item.status === "FAILED"
                                                                        ) &&
                                                                            item.taskCount <= 0 &&
                                                                            item.metadata?.isDirectDownloadRequest ===
                                                                                "MISS_MATCH_REGION" && (
                                                                                <StorageDownloadCdnTooltip />
                                                                            )}
                                                                        {renderJobStatus(item)}
                                                                    </Grid>
                                                                </Grid>
                                                                {item.tasks && getJobStatus(item) === "SUCCEED" && (
                                                                    <Button
                                                                        size="small"
                                                                        color="greySecondary"
                                                                        startIcon={<IconDownload size="16" />}
                                                                        onClick={(e) => {
                                                                            e.preventDefault();
                                                                            onClickJobDownload(item);
                                                                        }}
                                                                    >
                                                                        {t(
                                                                            `common::label::Download${
                                                                                getFileCount(item) > 1 ? " All" : ""
                                                                            }`,
                                                                            `Download${
                                                                                getFileCount(item) > 1 ? " All" : ""
                                                                            }`,
                                                                        )}
                                                                    </Button>
                                                                )}
                                                            </Grid>
                                                        </Stack>
                                                    )}
                                                    {item.tasks && item.isFolded && (
                                                        <Stack py={1.5} gap={2}>
                                                            <Box>
                                                                <Grid container flexWrap={"nowrap"}>
                                                                    <Grid item>
                                                                        {getJobFileIcon(item, item.tasks[0])}
                                                                    </Grid>
                                                                    <Grid
                                                                        item
                                                                        sx={{ display: "flex", flexDirection: "row" }}
                                                                    >
                                                                        <GridNameEllipsis item maxWidth={"250px"}>
                                                                            <Typography variant="body3_400">
                                                                                {getJobRepresentiveName(item)}
                                                                            </Typography>
                                                                        </GridNameEllipsis>
                                                                        {getFileCount(item) > 1 && (
                                                                            <>
                                                                                <Typography variant="body3_400">
                                                                                    <Trans
                                                                                        i18nKey="common::label::and <emp>{{count}}</emp> others"
                                                                                        components={{
                                                                                            emp: (
                                                                                                <Typography
                                                                                                    variant="body3_700"
                                                                                                    color="primary.main"
                                                                                                ></Typography>
                                                                                            ),
                                                                                        }}
                                                                                        values={{
                                                                                            count:
                                                                                                getFileCount(item) - 1,
                                                                                        }}
                                                                                    />
                                                                                </Typography>
                                                                            </>
                                                                        )}
                                                                    </Grid>
                                                                </Grid>
                                                                <Grid
                                                                    container
                                                                    flexWrap={"nowrap"}
                                                                    gap={1}
                                                                    mt={2}
                                                                    flexDirection={"column"}
                                                                >
                                                                    {item.tasks.map((task) => (
                                                                        <Grid
                                                                            container
                                                                            justifyContent={"space-between"}
                                                                        >
                                                                            <Grid
                                                                                item
                                                                                sx={{ display: "flex" }}
                                                                                gap={theme.spacing(1)}
                                                                            >
                                                                                <Grid item>
                                                                                    <IconHierarchyFolded
                                                                                        size={6}
                                                                                        htmlColor={
                                                                                            theme.palette
                                                                                                .greySecondary[500]
                                                                                        }
                                                                                    />
                                                                                </Grid>
                                                                                <Grid item>
                                                                                    <IconZip
                                                                                        size={16}
                                                                                        htmlColor={
                                                                                            theme.palette
                                                                                                .greySecondary[500]
                                                                                        }
                                                                                    />
                                                                                </Grid>
                                                                                <GridNameEllipsis
                                                                                    item
                                                                                    maxWidth={"300px"}
                                                                                >
                                                                                    <Typography variant="body2_500">
                                                                                        {getJobFileName(item, task)}
                                                                                    </Typography>
                                                                                </GridNameEllipsis>
                                                                            </Grid>
                                                                            {task.status === "FAILED" &&
                                                                                task.metadata?.error &&
                                                                                task.metadata?.error?.code && (
                                                                                    <Grid item>
                                                                                        <Tooltip
                                                                                            title={t(
                                                                                                `common::code::${task.metadata?.error?.code}`,
                                                                                                task.metadata?.error
                                                                                                    ?.code,
                                                                                            )}
                                                                                        >
                                                                                            <IconButton
                                                                                                circled
                                                                                                size="small"
                                                                                            >
                                                                                                <IconErrorCircle
                                                                                                    size={16}
                                                                                                    color="error"
                                                                                                />
                                                                                            </IconButton>
                                                                                        </Tooltip>
                                                                                    </Grid>
                                                                                )}
                                                                            {task?.status === "SUCCEED" &&
                                                                                task?.metadata?.archiveFileName && (
                                                                                    <Tooltip
                                                                                        title={t(
                                                                                            `common::label::Download`,
                                                                                            `Download`,
                                                                                        )}
                                                                                    >
                                                                                        <IconButton
                                                                                            size={"small"}
                                                                                            circled
                                                                                            onClick={(e) => {
                                                                                                e.preventDefault();
                                                                                                onClickDownload(
                                                                                                    item.jobId,
                                                                                                    task.name,
                                                                                                );
                                                                                            }}
                                                                                        >
                                                                                            <IconDownload size={16} />
                                                                                        </IconButton>
                                                                                    </Tooltip>
                                                                                )}
                                                                        </Grid>
                                                                    ))}
                                                                </Grid>
                                                            </Box>
                                                        </Stack>
                                                    )}
                                                </Grid>
                                            </Grid>
                                        </Box>
                                    </Collapse>
                                </Box>
                            );
                        })}
                    </>
                ) : (
                    <JobEmptyList />
                )}
            </Stack>
            {downloadJobs.data?.length > 0 && (
                <div className="panel-footer" style={{ flex: 0, paddingRight: "10px" }}>
                    <WithSelect
                        defaultValue={limit}
                        options={CONSTANTS("LIST_LIMITS")}
                        onChange={(option) => onChangeLimit(option.value)}
                    />
                    {viewmoreEnable ? (
                        <button
                            type={"button"}
                            className={"btn btn-outline-default"}
                            onClick={(e) => {
                                e.preventDefault();
                                onSubmit();
                            }}
                        >
                            <span>{`View More`}</span>
                            <IconChevronDown size={10} />
                        </button>
                    ) : (
                        ""
                    )}
                </div>
            )}
        </React.Fragment>
    );
};

export default withTranslation()(DownloadJobPanel);
